import React, { useEffect } from 'react'
import { connect } from 'react-redux'
import { createSelector } from '@reduxjs/toolkit'
import { withRouterProps } from '../../../utils/with-router-props'
import { Link } from 'react-router-dom'
import { apiResourceEndpoint, buildRoutePath, routes } from '../../../../constants/routes'
import { callApi, fetchApiDataIfNeeded } from '../../../../actions/api'
import { PaginationWrapper } from 'shs-pagination'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import moment from 'moment'
import AccountSidebar from '../../../shared/account-sidebar'

const selectPaginator = state => state.paginator.adminAccountImeis
const selectImeis = (state, accountId) => state.adminAccountImeis.filter(i => i.accountId === Number(accountId))
const makeImeis = createSelector(
    [selectImeis, selectPaginator],
    (imeis, paginator) => {
        if (paginator) {
            return imeis
                .filter(field => paginator.itemIds.indexOf(field.id) !== -1)
                .sort((a, b) => {
                    if (paginator.itemIds.indexOf(a.id) > paginator.itemIds.indexOf(b.id)) {
                        return 1
                    }
                    return -1
                })
        }
        return []
    }
)

const mapStateToProps = (state, ownProps) => {
    const imeis = makeImeis(state, ownProps.params.accountId)
    const account = state.adminAccounts.find(acc => acc.id === Number(ownProps.params.accountId))
    return { paginator: selectPaginator(state), imeis, account }
}

const AdminImeiList = ({ dispatch, paginator, imeis, params, account }) => {
    const accountId = params.accountId
    useEffect(() => {
        if (accountId) {
            dispatch(fetchApiDataIfNeeded(apiResourceEndpoint('admin/account-imeis', 'LIST', null), { filter: [`account_id|${accountId}`] }))
            dispatch(fetchApiDataIfNeeded(apiResourceEndpoint('admin/accounts', 'VIEW', accountId)))
        }
    }, [dispatch, accountId])

    // gates whether an installer can pick this unit during an install
    const handleToggleAvailable = (accountImei) => {
        dispatch(callApi(apiResourceEndpoint('admin/account-imeis', 'UPDATE', accountImei.id), { available: !accountImei.available }, -1))
            .then((response) => {
                if (response !== 'SUCCESS') {
                    window.alert('Unable to update availability. Please try again.')
                }
            })
    }

    const handleRemove = (accountImei) => {
        if (!window.confirm(`Remove telematics unit ${accountImei.imei} from this account?`)) {
            return
        }
        dispatch(callApi(apiResourceEndpoint('admin/account-imeis', 'DELETE', accountImei.id), {}, -1))
            .then((response) => {
                if (response !== 'SUCCESS') {
                    window.alert('Unable to remove this telematics unit. Please try again.')
                    return
                }
                // row already left the store via removeItem — refetch just to fix paginator totals
                dispatch(fetchApiDataIfNeeded(apiResourceEndpoint('admin/account-imeis', 'LIST', null), { filter: [`account_id|${accountId}`] }, true))
            })
    }

    const renderImeiRow = (accountImei, index) => {
        return (
            <tr key={accountImei.id}>
                <td className="index mobile-hide">{index}.</td>
                <td className="main">{accountImei.imei}</td>
                <td className="main">
                    <span className="clickable" onClick={() => handleToggleAvailable(accountImei)}>
                        {accountImei.available ? 'Yes' : 'No'}
                    </span>
                </td>
                <td className="main mobile-hide">{moment(accountImei.updatedAt).format('M/D/YY')}</td>
                <td className="main">
                    <span className="icon-action" title="Remove from account" onClick={() => handleRemove(accountImei)}>
                        <FontAwesomeIcon icon={['far', 'trash-can']} />
                    </span>
                </td>
            </tr>
        )
    }

    const handlePaginationUpdate = (paginationParams) => {
        if (accountId) {
            const paramsWithFilter = { ...paginationParams, filter: [`account_id|${accountId}`] }
            dispatch(fetchApiDataIfNeeded(apiResourceEndpoint(`admin/account-imeis`, 'LIST'), paramsWithFilter))
        }
    }

    const renderImeis = () => {
        if (!paginator) {
            return null
        }
        const offset = (paginator.currentPage - 1) * paginator.perPage + 1
        return (
            <PaginationWrapper
                paginator={paginator}
                controls={{ search: { enabled: true, collapsed: true } }}
                callbackFX={handlePaginationUpdate}>
                <table className="table striped">
                    <thead>
                        <tr>
                            <th className="mobile-hide">#</th>
                            <th>Telematic # (IMEI)</th>
                            <th>Available</th>
                            <th className="mobile-hide">Updated</th>
                            <th>&nbsp;</th>
                        </tr>
                    </thead>
                    <tbody>
                        {imeis.map((accountImei, idx) => renderImeiRow(accountImei, idx + offset))}
                    </tbody>
                </table>
            </PaginationWrapper>
        )
    }

    return (
        <div className="admin-dashboard">
            <div className="header">
                <div className="container">
                    <div className="header-details">
                        <h1>Dashboard for {account?.name}</h1>
                        <h2>Manage Imeis</h2>
                    </div>
                </div>
            </div>
            <div className="container">
                <div className="sidebar-layout">
                    <div className="sidebar box">
                        <AccountSidebar account={account} />
                    </div>
                    <div className="main-content box">
                        <Link
                            to={buildRoutePath(routes.adminImeiCreate.path, { accountId })}
                            className="clickable"
                            style={{ marginTop: '1rem', display: 'inline-block' }}>
                            + Add Telematics Unit
                        </Link>
                        { renderImeis() }
                    </div>
                </div>
            </div>
        </div>
    )
}

export default withRouterProps(connect(mapStateToProps)(AdminImeiList))
